import { useState } from "react";
import { ArrowRight, Loader2, AlertTriangle, Sparkles } from "lucide-react";
import api, { formatErr } from "../lib/api";

/**
 * SuspectPromoteDialog — gates promotion behind a confirmed customer
 * name + contact, then triggers the atomic backend flow that creates
 * Customer + Lead and migrates gallery / comments / meetings.
 *
 * Required: customer_name (defaults to suspect.name).
 * Optional overrides: contact_name, position, phone, email, lead title.
 */
export default function SuspectPromoteDialog({ suspect, onPromoted, onCancel }) {
  const [form, setForm] = useState({
    customer_name: suspect?.name || "",
    customer_type: "individual",
    contact_name: suspect?.contact_name || "",
    contact_position: suspect?.contact_position || "",
    phone: suspect?.phone || "",
    email: suspect?.email || "",
    title: suspect?.name || "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const submit = async () => {
    if (!form.customer_name.trim()) {
      setErr("Customer name is required to promote a suspect.");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post(`/suspects/${suspect.id}/promote`, form);
      onPromoted?.(data);
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail, "Promote failed"));
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="suspect-promote-dialog">
      <div className="hv-card border-violet-500/40 bg-violet-500/5 p-3 text-xs flex items-start gap-2">
        <Sparkles className="w-4 h-4 text-[#A78BFA] shrink-0 mt-0.5" />
        <div className="text-[#cbd5e1]">
          Promoting <span className="font-bold text-[#A78BFA]">{suspect?.name}</span> will atomically:
          <ul className="list-disc ml-4 mt-1 space-y-0.5 text-[#a0aabf]">
            <li>Create a <strong>Customer</strong> record with the contact details below.</li>
            <li>Create a <strong>Lead</strong> with phase = lead_logged, linked to that customer.</li>
            <li>Migrate any gallery photos, comments, or meetings tagged to this suspect.</li>
          </ul>
        </div>
      </div>

      {err && (
        <div className="border border-rose-500/40 bg-rose-500/5 text-rose-300 text-xs p-2 rounded flex items-center gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5" /> {err}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <label className="block col-span-2">
          <span className="text-[10px] uppercase tracking-wider text-[#647087]">Customer name *</span>
          <input
            value={form.customer_name}
            onChange={(e) => set({ customer_name: e.target.value })}
            className="hv-input mt-1"
            data-testid="suspect-promote-customer-name"
            autoFocus
          />
        </label>
        <label className="block">
          <span className="text-[10px] uppercase tracking-wider text-[#647087]">Customer type</span>
          <select
            value={form.customer_type}
            onChange={(e) => set({ customer_type: e.target.value })}
            className="hv-input mt-1"
            data-testid="suspect-promote-customer-type"
          >
            <option value="individual">Individual</option>
            <option value="business">Business</option>
          </select>
        </label>
        <label className="block">
          <span className="text-[10px] uppercase tracking-wider text-[#647087]">Lead title</span>
          <input
            value={form.title}
            onChange={(e) => set({ title: e.target.value })}
            className="hv-input mt-1"
            data-testid="suspect-promote-title"
            placeholder="e.g. Singita — purification quote"
          />
        </label>
        <label className="block">
          <span className="text-[10px] uppercase tracking-wider text-[#647087]">Contact person</span>
          <input
            value={form.contact_name}
            onChange={(e) => set({ contact_name: e.target.value })}
            className="hv-input mt-1"
            data-testid="suspect-promote-contact-name"
          />
        </label>
        <label className="block">
          <span className="text-[10px] uppercase tracking-wider text-[#647087]">Position</span>
          <input
            value={form.contact_position}
            onChange={(e) => set({ contact_position: e.target.value })}
            className="hv-input mt-1"
            data-testid="suspect-promote-position"
          />
        </label>
        <label className="block">
          <span className="text-[10px] uppercase tracking-wider text-[#647087]">Phone</span>
          <input
            value={form.phone}
            onChange={(e) => set({ phone: e.target.value })}
            className="hv-input mt-1"
            data-testid="suspect-promote-phone"
          />
        </label>
        <label className="block">
          <span className="text-[10px] uppercase tracking-wider text-[#647087]">Email</span>
          <input
            value={form.email}
            onChange={(e) => set({ email: e.target.value })}
            className="hv-input mt-1"
            data-testid="suspect-promote-email"
          />
        </label>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <button onClick={onCancel} className="hv-btn hv-btn-ghost" data-testid="suspect-promote-cancel">Cancel</button>
        <button onClick={submit} disabled={busy} className="hv-btn hv-btn-primary" data-testid="suspect-promote-submit">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowRight className="w-3.5 h-3.5" />}
          Promote → Customer + Lead
        </button>
      </div>
    </div>
  );
}
